import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useLanguage } from '@/contexts/LanguageContext';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BASE_URL, STRIPE_PUBLISHABLE_KEY } from '@/config/api';
import { getShopOrders, getShopOrder, ShopOrder, ShopTicket } from '@/services/shopService';
import { QRCodeSVG } from 'qrcode.react';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  ShoppingBag,
  Tag,
  Ticket as TicketIcon,
  Loader2,
  X,
  Lock,
  Package,
  Receipt,
  CalendarDays,
  CheckCircle2,
  Circle,
  ChevronRight,
  FileText,
  Wallet,
} from 'lucide-react';

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

interface Product {
  id: string;
  title: string;
  description: string;
  quantity: number;
  price: number;
  totalPrice: number;
  type?: string | null;
  amount?: number | null;
  amount_min?: number | null;
  amount_max?: number | null;
  coach: {
    id: string;
    name: string;
  };
}

const isPassProduct = (product: Product) => product.type === 'pass';

const getPassRange = (product: Product): { min: number; max: number } => {
  const min = Number(product.amount_min ?? product.amount ?? product.price ?? 0);
  const max = Number(product.amount_max ?? product.amount ?? product.price ?? 0);
  return { min, max };
};

// Steps of 100 between min and max (min and max always included)
const getPassOptions = (product: Product): number[] => {
  const { min, max } = getPassRange(product);
  if (!min || !max || max <= min) {
    return min > 0 ? [min] : [];
  }
  const values: number[] = [];
  for (let v = min; v + 100 <= max; v += 100) {
    values.push(v);
  }
  values.push(max);
  return values;
};

const formatEur = (value: number) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(value);

const formatDate = (value: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const ShopPage: React.FC = () => {
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [buyButtonLoading, setBuyButtonLoading] = useState<{ [key: string]: boolean }>({});
  const [invoiceId, setInvoiceId] = useState<number | null>(null);
  const [passAmounts, setPassAmounts] = useState<{ [key: string]: string }>({});
  const [selectedAmount, setSelectedAmount] = useState<number>(0);

  // Orders
  const [activeTab, setActiveTab] = useState<'products' | 'orders'>('products');
  const [orders, setOrders] = useState<ShopOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [openOrder, setOpenOrder] = useState<ShopOrder | null>(null);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);
  const isMobile = useIsMobile();

  const fetchProducts = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/shop/products`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      const data = await response.json();
      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      throw err;
    }
  }, [token]);

  const fetchOrders = useCallback(async () => {
    try {
      setOrdersLoading(true);
      setOrdersError(null);
      const data = await getShopOrders(token);
      setOrders(data);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setOrdersError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setOrdersLoading(false);
    }
  }, [token]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await fetchProducts();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    })();
    fetchOrders();
  }, [fetchProducts, fetchOrders]);

  const handleBuyNow = async (product: Product) => {
    try {
      setBuyButtonLoading((prev) => ({ ...prev, [product.id]: true }));
      setSelectedProduct(product);
      setPaymentStatus('processing');

      const isPass = isPassProduct(product);
      const amount = isPass
        ? Number(passAmounts[product.id] ?? getPassOptions(product)[0] ?? 0)
        : product.price * product.quantity;
      setSelectedAmount(amount);

      const response = await fetch(`${BASE_URL}/api/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount,
          currency: 'EUR',
          productId: product.id,
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to create payment intent');
      }
      const data = await response.json();
      setClientSecret(data.clientSecret);
    } catch (err) {
      console.error('Error creating payment intent:', err);
      setPaymentStatus('error');
      setPaymentError(err instanceof Error ? err.message : 'Failed to process payment');
      toast({
        variant: 'destructive',
        title: t('payment.payment_failed'),
        description: err instanceof Error ? err.message : 'Failed to process payment',
      });
    } finally {
      setBuyButtonLoading((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  useEffect(() => {
    if (invoiceId) {
      navigate('/invoices/' + invoiceId);
    }
  }, [invoiceId]);

  const handlePaymentSuccess = () => {
    setPaymentStatus('success');
    toast({
      title: t('payment.payment_successful'),
      description: t('payment.success_description'),
    });

    setSelectedProduct(null);
    setClientSecret(null);
    setPaymentStatus('idle');
    fetchProducts().catch(() => undefined);
    fetchOrders();
  };

  const handlePaymentError = (errorMessage: string) => {
    setPaymentStatus('error');
    setPaymentError(errorMessage);
    toast({
      variant: 'destructive',
      title: t('payment.payment_failed'),
      description: errorMessage,
    });
  };

  const handleCancelPayment = () => {
    setSelectedProduct(null);
    setClientSecret(null);
    setPaymentStatus('idle');
    setPaymentError(null);
  };

  const handleOpenOrder = async (order: ShopOrder) => {
    setOpenOrder(order);
    if (order.tickets.length === 0) {
      try {
        setOrderDetailLoading(true);
        const detail = await getShopOrder(token, order.id);
        if (detail) {
          setOpenOrder(detail);
          setOrders((prev) => prev.map((o) => (o.id === detail.id ? detail : o)));
        }
      } catch (err) {
        console.error('Error fetching order detail:', err);
      } finally {
        setOrderDetailLoading(false);
      }
    }
  };

  const options: StripeElementsOptions | undefined = clientSecret
    ? {
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#8B5CF6',
            colorBackground: '#ffffff',
            colorText: '#1A1F2C',
          },
        },
        paymentMethodCreation: 'manual',
      }
    : undefined;

  const renderProducts = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      );
    }
    if (error) {
      return (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded">
          <strong className="font-bold mr-1">Error:</strong>
          <span className="block sm:inline">{error}</span>
        </div>
      );
    }
    if (clientSecret && selectedProduct) {
      return (
        <div className="max-w-3xl mx-auto w-full">
          <Card className="w-full">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>{t('payment.title')}</CardTitle>
                  <CardDescription>{t('payment.card_details')}</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={handleCancelPayment}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-6 p-4 bg-primary/5 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium">{selectedProduct.title}</h3>
                  <Badge variant="outline" className="ml-2">
                    {isPassProduct(selectedProduct)
                      ? t('shop.typePass')
                      : `${selectedProduct.quantity} ${t('shop.tickets')}`}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-4">{selectedProduct.description}</p>
                {!isPassProduct(selectedProduct) && (
                  <div className="flex justify-between text-xs">
                    <span>{t('shop.Subtotal')}</span>
                    <span>
                      {selectedProduct.price} EUR / {t('shop.ticket')}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-medium">
                  <span>{t('payment.total')}</span>
                  <span>{selectedAmount} EUR</span>
                </div>
              </div>
              <Elements stripe={stripePromise} options={options}>
                <CheckoutForm
                  productId={selectedProduct.id}
                  amount={selectedAmount}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  setInvoiceId={setInvoiceId}
                />
              </Elements>
            </CardContent>
            <div className="p-4 bg-muted/40 border-t flex items-center justify-center text-sm text-muted-foreground">
              <Lock className="h-4 w-4 mr-2" />
              {t('payment.secure_encryption')}
            </div>
          </Card>
        </div>
      );
    }
    if (products.length === 0) {
      return (
        <div className="text-center py-20">
          <ShoppingBag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-medium mb-2">{t('shop.noProducts')}</h2>
          <p className="text-muted-foreground">{t('shop.yourCoachHasNoProductsYet')}</p>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product) => {
          const isPass = isPassProduct(product);
          const { min, max } = isPass ? getPassRange(product) : { min: 0, max: 0 };
          const passOptions = isPass ? getPassOptions(product) : [];
          const chosenPassAmount = passAmounts[product.id] ?? String(passOptions[0] ?? '');
          return (
          <Card key={product.id} className="overflow-hidden hover:shadow-md transition-shadow flex flex-col h-full">
            <CardHeader className="bg-primary/5 pb-3">
              <div className="flex justify-between items-start gap-2">
                <CardTitle className="text-lg truncate">{product.title}</CardTitle>
                <Badge variant="outline" className="flex-shrink-0 ml-2">
                  {isPass ? t('shop.typePass') : `${product.quantity} ${t('shop.tickets')}`}
                </Badge>
              </div>
              <CardDescription className="truncate">{product.description}</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 flex-grow">
              {isPass ? (
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Wallet className="h-5 w-5 text-primary mr-2" />
                    <span className="text-sm whitespace-nowrap">
                      {formatEur(min)} – {formatEur(max)}
                    </span>
                  </div>
                  {passOptions.length > 1 ? (
                    <Select
                      value={chosenPassAmount}
                      onValueChange={(v) =>
                        setPassAmounts((prev) => ({ ...prev, [product.id]: v }))
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={t('shop.selectPassAmount')} />
                      </SelectTrigger>
                      <SelectContent>
                        {passOptions.map((value) => (
                          <SelectItem key={value} value={String(value)}>
                            {formatEur(value)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm font-medium">{formatEur(passOptions[0] ?? min)}</p>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex items-center mb-2">
                    <TicketIcon className="h-5 w-5 text-primary mr-2" />
                    <span className="text-sm whitespace-nowrap">
                      {t('shop.pricePerTicket')}: {product.price} EUR
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Tag className="h-5 w-5 text-primary mr-2" />
                    <span className="text-sm whitespace-nowrap">
                      {t('shop.totalPrice')}: {product.price * product.quantity} EUR
                    </span>
                  </div>
                </>
              )}
            </CardContent>
            <CardFooter className="bg-muted/40 border-t pt-4">
              <Button className="w-full" onClick={() => handleBuyNow(product)}>
                {buyButtonLoading[product.id] ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : t('shop.buyNow')}
              </Button>
            </CardFooter>
          </Card>
          );
        })}
      </div>
    );
  };

  const renderOrders = () => {
    if (ordersLoading) {
      return (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="ml-2">{t('common.loading')}</span>
        </div>
      );
    }
    if (ordersError) {
      return (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded">
          <span>{ordersError}</span>
        </div>
      );
    }
    if (orders.length === 0) {
      return (
        <div className="text-center py-20">
          <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-medium mb-2">{t('shop.noOrders')}</h2>
          <p className="text-muted-foreground">{t('shop.noOrdersDescription')}</p>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {orders.map((order) => {
          const isPass = order.productType === 'pass';
          const remaining = isPass
            ? Math.max(order.total - order.amountUsed, 0)
            : Math.max(order.ticketsTotal - order.ticketsUsed, 0);
          const percent = isPass
            ? (order.total > 0 ? (order.amountUsed / order.total) * 100 : 0)
            : (order.ticketsTotal > 0 ? (order.ticketsUsed / order.ticketsTotal) * 100 : 0);
          return (
            <button
              key={order.id}
              type="button"
              onClick={() => handleOpenOrder(order)}
              className="text-left group"
            >
              <Card className="h-full transition-shadow group-hover:shadow-md border-dashed">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="text-base truncate">{order.productTitle}</CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-1">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {formatDate(order.date)}
                        <span className="text-muted-foreground">·</span>
                        <Receipt className="h-3.5 w-3.5" />
                        {order.reference}
                      </CardDescription>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">
                      {order.total.toFixed(2)} {order.currency}
                    </span>
                    <Badge variant={remaining > 0 ? 'default' : 'secondary'}>
                      {isPass
                        ? `${remaining.toFixed(2)} / ${order.total.toFixed(2)} ${order.currency} ${t('shop.passRemaining')}`
                        : `${remaining} / ${order.ticketsTotal} ${t('shop.ticketsRemaining')}`}
                    </Badge>
                  </div>
                  {isPass && (
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{t('shop.passBadge')}</span>
                      <span>
                        {order.amountUsed.toFixed(2)} {order.currency} {t('shop.usedLabel')}
                      </span>
                    </div>
                  )}
                  <div className="w-full max-w-md mx-auto px-1 flex items-center justify-center">
                    <Progress value={percent} className="h-2" />
                  </div>
                </CardContent>
              </Card>
            </button>
          );
        })}
      </div>
    );
  };

  const isOpenOrderPass = openOrder?.productType === 'pass';
  const remainingInOpenOrder = openOrder
    ? isOpenOrderPass
      ? Math.max(openOrder.total - openOrder.amountUsed, 0)
      : Math.max(openOrder.ticketsTotal - openOrder.ticketsUsed, 0)
    : 0;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 container py-8 mt-16">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center mb-4">
              <ShoppingBag className="h-6 w-6 mr-2" />
              <CardTitle>{t('shop.title')}</CardTitle>
            </div>

            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'products' | 'orders')}>
              <TabsList className="grid w-full grid-cols-2 sm:w-auto sm:inline-flex mb-6">
                <TabsTrigger value="products" className="gap-2">
                  <ShoppingBag className="h-4 w-4" />
                  {t('shop.tabProducts')}
                </TabsTrigger>
                <TabsTrigger value="orders" className="gap-2">
                  <Package className="h-4 w-4" />
                  {t('shop.tabOrders')}
                  {orders.length > 0 && (
                    <Badge variant="secondary" className="ml-1">
                      {orders.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="products">{renderProducts()}</TabsContent>
              <TabsContent value="orders">{renderOrders()}</TabsContent>
            </Tabs>
          </CardHeader>
        </Card>
      </main>

      <Dialog open={!!openOrder} onOpenChange={(o) => !o && setOpenOrder(null)}>
        <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[600px]"}>
          <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {openOrder?.productTitle}
            </DialogTitle>
            <DialogDescription className="flex text-start gap-2 text-sm text-muted-foreground">
              {t('shop.orderNumber')} {openOrder?.reference} · {formatDate(openOrder?.date ?? null)}
            </DialogDescription>
          </DialogHeader>

          {openOrder && (
            <div className={isMobile ? "p-4" : ""}>
              <div className="rounded-lg border border-dashed p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {isOpenOrderPass ? t('shop.passSummary') : t('shop.ticketsSummary')}
                  </span>
                  <span className="text-sm font-semibold">
                    {isOpenOrderPass
                      ? `${remainingInOpenOrder.toFixed(2)} / ${openOrder.total.toFixed(2)} ${openOrder.currency}`
                      : `${remainingInOpenOrder} / ${openOrder.ticketsTotal}`}
                  </span>
                </div>
                <Progress
                  value={
                    isOpenOrderPass
                      ? openOrder.total > 0
                        ? (openOrder.amountUsed / openOrder.total) * 100
                        : 0
                      : openOrder.ticketsTotal > 0
                        ? (openOrder.ticketsUsed / openOrder.ticketsTotal) * 100
                        : 0
                  }
                  className="h-2"
                />
                {isOpenOrderPass && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t('shop.usedLabel')}</span>
                    <span className="font-medium">
                      {openOrder.amountUsed.toFixed(2)} {openOrder.currency}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t('payment.total')}</span>
                  <span className="font-medium">
                    {openOrder.total.toFixed(2)} {openOrder.currency}
                  </span>
                </div>
                {openOrder.invoiceId && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2"
                    onClick={() => navigate(`/invoices/${openOrder.invoiceId}`)}
                  >
                    <FileText className="h-4 w-4" />
                    {t('shop.viewInvoice')}
                  </Button>
                )}
                {remainingInOpenOrder > 0 && (
                  <div className="flex justify-center border-t pt-4">
                    <QRCodeSVG value={openOrder.id} size={180} level="M" />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {orderDetailLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : remainingInOpenOrder === 0 ? (
                  <p className="text-sm text-muted-foreground py-4">{t('shop.noTickets')}</p>
                ) : (
                  <ul className="divide-y rounded-md">
                    {openOrder.tickets.map((ticket: ShopTicket) => (
                      <li key={ticket.id} className="flex items-center justify-between gap-3 p-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {ticket.used ? (
                            <CheckCircle2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          ) : (
                            <Circle className="h-4 w-4 text-primary flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className={`text-sm truncate ${ticket.used ? 'text-muted-foreground line-through' : 'font-medium'}`}>
                              {ticket.code}
                            </p>
                            {ticket.used && (
                              <p className="text-xs text-muted-foreground truncate">
                                {formatDate(ticket.usedAt)}
                                {ticket.eventTitle ? ` · ${ticket.eventTitle}` : ''}
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge variant={ticket.used ? 'secondary' : 'default'} className="flex-shrink-0">
                          {ticket.used ? t('shop.ticketUsed') : t('shop.ticketAvailable')}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

interface CheckoutFormProps {
  productId: string;
  amount: number;
  onPaymentSuccess: () => void;
  onPaymentError: (message: string) => void;
  setInvoiceId: React.Dispatch<React.SetStateAction<number | null>>;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  productId,
  amount,
  onPaymentSuccess,
  onPaymentError,
  setInvoiceId,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useLanguage();
  const { token } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    setIsProcessing(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message || 'An unexpected error occurred');
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      });
      if (error) {
        throw new Error(error.message || 'Failed to create payment method');
      }

      const response = await fetch(`${BASE_URL}/api/shop/purchases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          productId,
          quantity: amount,
          amount,
          price: amount,
        }),
      });
      const result = await response.json();
      const newInvoiceId = result.invoiceId;
      if (!response.ok) {
        throw new Error(result.message || 'Payment failed');
      }

      if (result.status === 'requires_action') {
        const { error: confirmError } = await stripe.confirmCardPayment(result.clientSecret);
        if (confirmError) {
          throw new Error(confirmError.message || 'Payment confirmation failed');
        } else {
          setInvoiceId(newInvoiceId);
          onPaymentSuccess();
        }
      } else {
        setInvoiceId(newInvoiceId);
        onPaymentSuccess();
      }
    } catch (err) {
      console.error('Payment error:', err);
      onPaymentError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      <Button type="submit" className="w-full mt-6" disabled={!stripe || !elements || isProcessing}>
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('payment.processing')}
          </>
        ) : (
          t('payment.complete_payment')
        )}
      </Button>
    </form>
  );
};

export default ShopPage;
